import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationInfo } from '@lub0v/santa-utils';
import { parseGamePath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppSelector } from '@src/state/hooks';

type NotificationsState = {
  items: NotificationInfo[];
  loading: boolean;
  error: boolean;
  busy: boolean;
  unread: number;
  refresh: () => Promise<void>;
  markRead: (ids: number[]) => Promise<boolean>;
};
const Context = createContext<NotificationsState | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const userId = useAppSelector((state) => state.session.user?.id);
  const { i18n } = useTranslation();
  const identity = `${userId ?? 'visitor'}:${i18n.language}`;
  const [data, setData] = useState<{
    identity: string;
    items: NotificationInfo[];
    error: boolean;
  }>({ identity, items: [], error: false });
  const [loadedIdentity, setLoadedIdentity] = useState<string>();
  const [busyIdentity, setBusyIdentity] = useState<string>();
  const gameKey = typeof window !== 'undefined' ? window.__ssBoxKey : undefined;
  const items = (data.identity === identity && userId ? data.items : []).filter((item) => {
    if (!gameKey) return true;
    try {
      const url = new URL(item.url, window.location.origin);
      return parseGamePath(url.pathname, url.origin === window.location.origin ? gameKey : undefined)?.key === gameKey;
    } catch {
      return false;
    }
  });
  const error = data.identity === identity && data.error;
  const loading = Boolean(userId) && loadedIdentity !== identity;
  const busy = busyIdentity === identity;
  const mutating = useRef(false);
  const revision = useRef(0);
  const refresh = useCallback(async () => {
    if (!userId || mutating.current) return;
    const request = ++revision.current;
    try {
      const result = await santaClient.getAccountNotifications();
      if (request !== revision.current) return;
      if (!result.ok) throw new Error('notifications failed');
      setData({ identity, items: result.data ?? [], error: false });
    } catch {
      if (request === revision.current)
        setData((previous) => ({
          identity,
          items: previous.identity === identity ? previous.items : [],
          error: true,
        }));
    } finally {
      if (request === revision.current) setLoadedIdentity(identity);
    }
  }, [userId, identity]);

  useEffect(() => {
    // Keep profile changes from remounting routes (including in-progress forms).
    const initial = window.setTimeout(() => {
      setBusyIdentity(undefined);
      void refresh();
    }, 0);
    const invalidate = () => {
      ++revision.current;
      mutating.current = false;
    };
    const updateVisible = () => {
      if (!document.hidden) void refresh();
    };
    const timer = window.setInterval(updateVisible, 30000);
    window.addEventListener('focus', updateVisible);
    document.addEventListener('visibilitychange', updateVisible);
    return () => {
      invalidate();
      window.clearTimeout(initial);
      window.clearInterval(timer);
      window.removeEventListener('focus', updateVisible);
      document.removeEventListener('visibilitychange', updateVisible);
    };
  }, [refresh]);

  async function markRead(ids: number[]) {
    if (!userId || mutating.current || !ids.length) return false;
    mutating.current = true;
    setBusyIdentity(identity);
    const request = ++revision.current;
    try {
      const result = await santaClient.readAccountNotifications({ ids });
      if (request !== revision.current) return false;
      if (!result.ok) throw new Error('mark read failed');
      setData({ identity, items: result.data ?? [], error: false });
      return true;
    } catch {
      if (request === revision.current)
        setData((previous) => ({
          identity,
          items: previous.identity === identity ? previous.items : [],
          error: true,
        }));
      return false;
    } finally {
      if (request === revision.current) {
        mutating.current = false;
        setBusyIdentity(undefined);
        setLoadedIdentity(identity);
      }
    }
  }

  return (
    <Context.Provider
      value={{
        items,
        loading,
        error,
        busy,
        unread: items.filter((item) => !item.is_viewed).length,
        refresh,
        markRead,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useNotifications() {
  const value = useContext(Context);
  if (!value) throw new Error('NotificationsProvider is missing');
  return value;
}
